import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AppMaterialModule } from '../../../../../app-material.module';
import { AuthService } from '../../../../../shared/services/auth.service';
import { NavigationService } from '../../../../../shared/services/navigation.service';
import { UsageLimitService } from '../../../../../shared/services/usage-limit/usage-limit.service';
import { openComingSoonDialog } from '../../../../../shared/components/dialogs/coming-soon-dialog/coming-soon-dialog.component';
import { ComingSoonTrafficSource } from '../../../../../shared/components/dialogs/coming-soon-dialog/coming-soon-traffic-source';
import {
    ForbiddenLayout,
    PREMIUM_GATED_NAV_ROUTES,
} from '../../../dashboard/dashboard-forbidden/dashboard-forbidden.models';
import { V2ShellLayoutStore } from '../../services/v2-shell-layout.store';
import { V2NavExpandableSection, V2ShellNavService } from '../../services/v2-shell-nav.service';
import { RedeAdsEnum } from '../../../../../shared/enum/rede-ads.enum';
import { MANAGEMENT_ADS_NAV_ITEMS, ManagementAdsNavItem } from './management-ads-nav.utils';
import { savedFinancialDashboardRoute } from '../../../../analytics/financial-dashboard-new/models/layout/consts/financial-dashboard-version';
import { StorageService } from '../../../../../shared/services/storage/storage.service';
import { environment } from '../../../../../../environments/environment';

@Component({
    selector: 'app-v2-shell-sidebar-nav',
    standalone: true,
    imports: [CommonModule, RouterModule, AppMaterialModule, TranslocoPipe],
    templateUrl: './v2-shell-sidebar-nav.component.html',
    styleUrl: './v2-shell-sidebar-nav.component.scss',
})
export class V2ShellSidebarNavComponent {
    readonly ForbiddenLayout = ForbiddenLayout;
    readonly RedeAdsEnum = RedeAdsEnum;
    readonly managementNavItems = MANAGEMENT_ADS_NAV_ITEMS;
    public readonly canSeeRatoeira: boolean = this.authService.canSeeRatoeira();
    public isMinimumPlan = false;

    @Input() isAdm = false;
    @Input() habilitarGerenciadorAnuncios = false;

    @Output() toggleAi = new EventEmitter<void>();

    constructor(
        public navService: V2ShellNavService,
        private layoutStore: V2ShellLayoutStore,
        public authService: AuthService,
        private router: Router,
        private navigationService: NavigationService,
        private dialog: MatDialog,
        private transloco: TranslocoService,
        private storage: StorageService,
        private usageLimitService: UsageLimitService,
    ) {
        this.usageLimitService.usage$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.isMinimumPlan = this.usageLimitService.isMinimumPlan();
        });
    }

    get isCollapsed(): boolean {
        return this.layoutStore.isDesktop && this.layoutStore.isCollapsed;
    }

    get expandedSections() {
        return this.navService.expandedSections;
    }

    isSectionActive(section: Parameters<V2ShellNavService['isSectionActive']>[0]): boolean {
        return this.navService.isSectionActive(section);
    }

    toggleSection(section: V2NavExpandableSection): void {
        this.navService.toggleSection(section);
    }

    expandirMenu(section: 'notificacoes' | 'checkout' | 'integracoes' | 'dashboard' | 'avancado'): void {
        this.navService.expandirMenu(section);
    }

    onNavClick(premiumGated?: ForbiddenLayout): void {
        if (premiumGated === ForbiddenLayout.DASHBOARD) {
            void this.router.navigate([savedFinancialDashboardRoute(this.storage)]);
        } else if (premiumGated !== undefined) {
            void this.router.navigate(PREMIUM_GATED_NAV_ROUTES[premiumGated].allowed);
        }

        this.navService.afterNavigate();
    }

    tooltipWhenCollapsed(key: string): string {
        if (!this.layoutStore.isDesktop || !this.layoutStore.isCollapsed) {
            return '';
        }

        return this.transloco.translate(key);
    }

    isTagRatoeiraActive(): boolean {
        const baseUrl = (this.router.url || '').split('?')[0];
        return baseUrl.startsWith('/v2/tag-ratoeira') && !baseUrl.startsWith('/v2/tag-ratoeira/forbidden');
    }

    canShowFontesTrafego(): boolean {
        return this.authService.canAccessFontesTrafego();
    }

    get isStaffSession(): boolean {
        return this.authService.getImpersonationInfo().isImpersonating;
    }

    isUrlProdutorActive(): boolean {
        const baseUrl = (this.router.url || '').split('?')[0];
        return (
            baseUrl.startsWith('/v2/url-produtor') ||
            baseUrl.startsWith('/url-produtor') ||
            baseUrl.startsWith('/v2/gerador-url-campanha/produtor') ||
            baseUrl.startsWith('/gerador-url-campanha/produtor')
        );
    }

    isCampaignCreatorActive(): boolean {
        const baseUrl = (this.router.url || '').split('?')[0];
        return baseUrl.startsWith('/v2/campaign-creator');
    }

    isCampaignCreatorAvailable(): boolean {
        return this.authService.isRedeAdsAtiva(RedeAdsEnum.GOOGLE_ADS);
    }

    acessarUrlProdutor(): void {
        this.onNavClick();
        this.navigationService.goToContextual('url-produtor');
    }

    abrirModalRedeEmBreve(source: ComingSoonTrafficSource): void {
        if (source === 'meta') {
            this.navigationService.goTo('/v2/analytics/management-ads/meta-ads');
            this.onNavClick();
            return;
        }

        if (this.layoutStore.isMobile) {
            this.layoutStore.closeSidenav();
        }
        openComingSoonDialog(this.dialog, source);
        this.onNavClick();
    }

    onManagementNetworkClick(item: ManagementAdsNavItem): void {
        if (!this.isManagementNetworkAvailable(item)) {
            return;
        }

        if (item.redeAds === RedeAdsEnum.META_ADS) {
            this.abrirModalRedeEmBreve('meta');
            return;
        }

        if (item.comingSoonSource) {
            this.abrirModalRedeEmBreve(item.comingSoonSource);
            return;
        }

        this.navigationService.goTo(item.route);
        this.onNavClick();
    }

    isManagementNetworkAvailable(item: ManagementAdsNavItem): boolean {
        return this.authService.isRedeAdsAtiva(item.redeAds);
    }

    isManagementNetworkComingSoon(item: ManagementAdsNavItem): boolean {
        if ('redeAds' in item && item.redeAds === RedeAdsEnum.META_ADS) {
            return false;
        }

        return item.comingSoonSource != null;
    }

    showManagementSection(): boolean {
        if (this.isMinimumPlan) {
            return false;
        }

        return this.isAdm || this.habilitarGerenciadorAnuncios;
    }

    canShowTagRatoeiraMenu(): boolean {
        return true;
    }

    canShowWhatsappMenu(): boolean {
        return environment.whatsappModuleEnabled;
    }

    isFinancialDashboardActive(): boolean {
        const baseUrl = (this.router.url || '').split('?')[0];
        return baseUrl.startsWith('/v2/analytics/financial-dashboard') || baseUrl.startsWith('/v2/analytics/forbidden');
    }
}
